import vegaEmbed from 'vega-embed';
import React, { useEffect, useRef, useState } from 'react';
import { Box, IconButton, Menu, MenuItem, Tooltip, Typography } from '@mui/material';
import { MdFullscreen } from "react-icons/md";
import KPICard from './KPICard';
import ChartError from './ChartError';
import ChartSkeleton from './ChartSkeleton';
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";

// QFlow theme configurations
const qflowLightTheme = {
  background: '#ffffff',
  title: { color: '#2D3748', fontSize: 14, fontWeight: 600 },
  style: {
    'guide-label': { fill: '#4A5568', fontSize: 12 },
    'guide-title': { fill: '#2D3748', fontSize: 13 }
  },
  axis: { 
    labelColor: '#4A5568',
    labelFontSize: 11,
    titleColor: '#2D3748',
    titleFontSize: 12,
    gridColor: '#E2E8F0',
    domainColor: '#CBD5E0'
  },
  legend: {
    labelColor: '#4A5568',
    labelFontSize: 11,
    titleColor: '#2D3748',
    titleFontSize: 12
  },
  range: {
    category: ['#4DA685', '#48BB78', '#38A169', '#68D391', '#9AE6B4', '#C6F6D5'],
    diverging: ['#F56565', '#FBD38D', '#48BB78']
  }
};

const qflowDarkTheme = {
  background: '#1A202C',
  title: { color: '#F7FAFC', fontSize: 14, fontWeight: 600 },
  style: {
    'guide-label': { fill: '#CBD5E0', fontSize: 12 },
    'guide-title': { fill: '#F7FAFC', fontSize: 13 }
  },
  axis: {
    labelColor: '#CBD5E0',
    labelFontSize: 11,
    titleColor: '#F7FAFC',
    titleFontSize: 12,
    gridColor: '#2D3748',
    domainColor: '#4A5568'
  },
  legend: {
    labelColor: '#CBD5E0',
    labelFontSize: 11,
    titleColor: '#F7FAFC',
    titleFontSize: 12
  },
  range: {
    category: ['#68D391', '#48BB78', '#38A169', '#4DA685', '#9AE6B4', '#C6F6D5'],
    diverging: ['#FC8181', '#FBD38D', '#68D391']
  }
};

export default function QPilotChart({ spec, theme = 'light', isMobile = false, isFocusMode = false, onError, onWidthOverflow }) {
  const containerRef = useRef(null);
  const viewRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [isFullscreen, setIsFullscreen] = useState(false);

  console.log('QPilotChart render - spec:', spec);
  console.log('QPilotChart render - loading state:', loading);

  // Track fullscreen state
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
    };
  }, []);

  // Handle single value cards
  if (spec?.type === 'single_value') {
    return <KPICard spec={spec} />;
  }

  // Handle table type as a special case
  if (spec?.type === 'table') {
    // For now, we'll show a message that table view is coming soon
    return (
      <Box sx={{ my: 2, p: 2, bgcolor: 'grey.50', borderRadius: 2, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          Table visualization coming soon. Data is available in the text response above.
        </Typography>
      </Box>
    );
  }

  useEffect(() => {
    console.log('QPilotChart useEffect triggered');
    console.log('containerRef.current:', containerRef.current);
    console.log('spec:', spec);
    
    if (!spec || !containerRef.current) {
      console.log('Early return - missing spec or containerRef');
      return;
    }

    // Create a container div that Vega can own completely
    const vegaContainer = document.createElement('div');
    vegaContainer.style.width = '100%';
    vegaContainer.style.height = '100%';
    containerRef.current.appendChild(vegaContainer);

    const embedChart = async () => {
      try {
        setLoading(true);
        setError(null);

        // Validate spec
        if (!spec.$schema || !spec.data) {
          throw new Error('Invalid chart specification');
        }

        // Apply theme
        const selectedTheme = theme === 'dark' ? qflowDarkTheme : qflowLightTheme;

        // Calculate padding
        const chartPadding = spec.padding || (isMobile ? { left: 15, right: 15, top: 5, bottom: 5 } : { left:150, right: 25, top: 10, bottom: 10 });
        const horizontalPadding = (chartPadding.left || 0) + (chartPadding.right || 0);
        
        // Responsive sizing - adjust width to account for padding and fullscreen mode
        let chartWidth, chartHeight;
        
        if (isFullscreen) {
          // Use full screen dimensions with some padding
          chartWidth = window.innerWidth - 100;
          chartHeight = window.innerHeight - 100;
        } else if (isMobile) {
          chartWidth = Math.min(300 - horizontalPadding, window.innerWidth - 80 - horizontalPadding);
          chartHeight = 250;
        } else {
          chartWidth = Math.max(100, (spec.width || 600) - horizontalPadding);
          chartHeight = spec.height || 400;
        }

        const responsiveSpec = {
          ...spec,
          width: chartWidth,
          height: chartHeight,
          padding: chartPadding,
          autosize: {
            type: "fit",
            contains: "padding"
          }
        };
        
        console.log('QPilotChart - originalWidth:', spec.width, 'adjustedWidth:', responsiveSpec.width, 'padding:', chartPadding);

        // Mobile-specific interactions
        const mobileInteractions = isMobile ? {
          params: [{
            name: "grid",
            select: "interval",
            bind: "scales"
          }]
        } : {};

        const result = await vegaEmbed(vegaContainer, responsiveSpec, {
          actions: false, // Disable all actions for now
          tooltip: true,
          config: selectedTheme
        });

        viewRef.current = result.view;
        console.log('Chart rendered successfully');
        setLoading(false);

        // Check for width overflow and trigger callback if needed
        if (onWidthOverflow && containerRef.current && !isFocusMode) {
          // Small delay to ensure chart is fully rendered
          setTimeout(() => {
            const container = containerRef.current;
            const chartElement = container.querySelector('.vega-embed');
            if (chartElement && chartElement.scrollWidth > container.clientWidth) {
              onWidthOverflow();
            }
          }, 100);
        }
      } catch (err) {
        console.error('Chart rendering error:', err);
        setError(err.message || 'Failed to render chart');
        setLoading(false);
        onError?.(err);
      }
    };

    embedChart();

    return () => {
      // Clean up the container we created
      if (vegaContainer.parentNode === containerRef.current) {
        containerRef.current.removeChild(vegaContainer);
      }
      // Also finalize the view if it exists
      if (viewRef.current) {
        viewRef.current.finalize();
      }
    };
  }, [spec, theme, isMobile, isFocusMode, isFullscreen, onError]);

  const handleExport = async (format) => {
    if (!viewRef.current) return;

    try {
      if (format === 'png') {
        const url = await viewRef.current.toImageURL('png', 2);
        const link = document.createElement('a');
        link.href = url;
        link.download = `qflow-chart-${Date.now()}.png`;
        link.click();
      } else if (format === 'csv') {
        const data = viewRef.current.data('data_0') || viewRef.current.data('source_0') || viewRef.current.data('data');
        if (!data) {
          console.error('No data found in chart');
          return;
        }
        const csv = convertToCSV(data);
        const blob = new Blob([csv], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `qflow-data-${Date.now()}.csv`;
        link.click();
        URL.revokeObjectURL(url);
      }
    } catch (err) {
      console.error('Export error:', err);
    }
    setAnchorEl(null);
  };

  const handleFullscreen = () => {
    if (containerRef.current) {
      if (document.fullscreenElement) {
        document.exitFullscreen();
      } else {
        containerRef.current.requestFullscreen();
      }
    }
  };

  if (error) {
    return <ChartError error={error} />;
  }

  return (
    <Box 
      sx={{ 
        position: 'relative', 
        my: isFocusMode ? 2 : 0.5,
        '& .vega-embed': {
          width: '100%'
        }
      }}
    >
      {/* Action buttons - top right overlaid */}
      <Box sx={{ 
        position: 'absolute', 
        top: 8, 
        right: 8, 
        zIndex: 1,
        display: 'flex',
        gap: 0.5
      }}>
        {!isMobile && (
          <Tooltip title="Fullscreen">
            <IconButton 
              size="small" 
              onClick={handleFullscreen} 
              sx={{ 
                border: '1px solid',
                borderColor: 'primary.main',
                borderRadius: '6px',
                color: 'primary.main',
                backgroundColor: 'transparent',
                width: 28,
                height: 28,
                '&:hover': {
                  backgroundColor: 'primary.main',
                  color: 'white'
                }
              }}
            >
              <MdFullscreen size="1em" />
            </IconButton>
          </Tooltip>
        )}
        <Tooltip title="Export">
          <IconButton 
            size="small" 
            onClick={(e) => setAnchorEl(e.currentTarget)}
            sx={{ 
              border: '1px solid',
              borderColor: 'primary.main',
              borderRadius: '6px',
              color: 'primary.main',
              backgroundColor: 'transparent',
              width: 28,
              height: 28,
              '&:hover': {
                backgroundColor: 'primary.main',
                color: 'white'
              }
            }}
          >
            <FileDownloadOutlinedIcon sx={{fontSize: "1em"}} />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Chart container */}
      <Box 
        ref={containerRef} 
        sx={{ 
          width: '100%',
          minHeight: isFocusMode ? (isMobile ? 250 : 400) : 120,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          position: 'relative',
          backgroundColor: isFullscreen ? 'white' : 'transparent',
          '& .vega-embed': {
            maxWidth: '100%'
          },
          // Fullscreen specific styles
          ...(isFullscreen && {
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            zIndex: 9999,
            padding: 2
          })
        }}
        role="img"
        aria-label={`${spec.type || 'Chart'}: ${spec.metadata?.question || 'Data visualization'}`}
      >
        {loading && (
          <Box sx={{ position: 'absolute', inset: 0 }}>
            <ChartSkeleton height="100%" />
          </Box>
        )}
      </Box>

      {/* Export menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <MenuItem onClick={() => handleExport('png')}>Export as PNG</MenuItem>
        <MenuItem onClick={() => handleExport('csv')}>Export Data (CSV)</MenuItem>
      </Menu>
    </Box>
  );
}

// Utility function to convert data to CSV
function convertToCSV(data) {
  if (!data || data.length === 0) return '';
  
  // Get headers from the first object
  const headers = Object.keys(data[0]);
  
  // Create CSV rows
  const rows = data.map(row => 
    headers.map(header => {
      const value = row[header];
      // Handle values that contain commas or quotes
      if (value === null || value === undefined) return '';
      const stringValue = String(value);
      if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
        return `"${stringValue.replace(/"/g, '""')}"`;
      }
      return stringValue;
    }).join(',')
  );
  
  // Combine headers and rows
  return [headers.join(','), ...rows].join('\n');
}